import { trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { AssetDto, GalleryDto, TenantDto, UpdateGalleryBodyDto } from '@nusign/picflow-backend-types';
import { combineLatest, distinctUntilChanged, filter, map, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { coverDescription, coverTitle } from '../../../animations/gallery-cover.animation';
import { billingScopes } from '../../../billing-consts';
import { Rect } from '../../../classes/rect.class';
import { ColorPickerComponent } from '../../../color-picker/color-picker.component';
import { videoFormats } from '../../../consts';
import { GROWTHBOOK } from '../../../growthbook';
import { canDisplayAsset } from '../../../lib/can-display-asset';
import { changeCoverAsset } from '../../../lib/change-cover-asset';
import { copy } from '../../../lib/copy';
import { debounce } from '../../../lib/debounce';
import { fixContentEditableStyles } from '../../../lib/fix-contenteditable-styles';
import { getHtmlText } from '../../../lib/get-html-text';
import { hexToRgb, hexToRgba } from '../../../lib/hex-to-rgb';
import { sleep } from '../../../lib/sleep';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { PICFLOW_CHECKOUT } from '../../../picflow-checkout';
import { AssetService } from '../../../services/asset.service';
import { CleanUserGeneratedHtmlService } from '../../../services/clean-user-generated-html.service';
import { DialogService } from '../../../services/dialog.service';
import { DragDropService } from '../../../services/drag-drop.service';
import { GalleryMetaImageUploadService } from '../../../services/gallery-meta-image-upload.service';
import { GalleryThemeService } from '../../../services/gallery-theme.service';
import { GalleryService } from '../../../services/gallery.service';
import { MetaService } from '../../../services/meta.service';
import { PermissionService } from '../../../services/permission.service';
import { TenantService } from '../../../services/tenant.service';
import { ToastService } from '../../../services/toast.service';
import { DropComponent } from '../../../shared-extra/components/drop/drop.component';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { DragItemDirective } from '../../../shared/components/drag-drop/drag-item.directive';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { GalleryCoverUrlPipe } from '../../../shared/pipes/gallery-cover-url.pipe';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';
import { ApplyTypographyStylesDirective } from '../../directives/apply-typography-styles.directive';
import { calcLegacyTheme } from '../../lib/calc-legacy-theme';
import { GalleryThemeMenuComponent } from '../../menus/gallery-theme-menu/gallery-theme-menu.component';
import { CoverDesignComponent } from '../cover-design/cover-design.component';
import { ThemeCoverSelectItemComponent } from '../theme-cover-select-item/theme-cover-select-item.component';

@Component({
  selector: 'app-gallery-cover',
  templateUrl: './gallery-cover.component.html',
  styleUrls: ['./gallery-cover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [trigger('title', coverTitle), trigger('description', coverDescription)],
  imports: [
    DropComponent,
    WysiwygDirective,
    ResizeDirective,
    ButtonDirective,
    SvgComponent,
    ButtonMenuItemComponent,
    TooltipDirective,
    FormsModule,
    ColorPickerComponent,
    GalleryCoverUrlPipe,
    LoadingIndicatorComponent,
    ContextMenuComponent,
    ApplyTypographyStylesDirective,
    CoverDesignComponent,
    ThemeCoverSelectItemComponent,
    DragItemDirective,
  ],
})
export class GalleryCoverComponent implements OnChanges, OnDestroy {
  private dialog = inject(DialogService);
  private galleryService = inject(GalleryService);
  private assetService = inject(AssetService);
  private galleryThemeService = inject(GalleryThemeService);
  private tenantService = inject(TenantService);
  private meta = inject(MetaService);
  private cleanUserGeneratedHtmlService = inject(CleanUserGeneratedHtmlService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private galleryMetaImageUploadService = inject(GalleryMetaImageUploadService);
  private toasts = inject(ToastService);
  private permissionService = inject(PermissionService);
  private checkoutService = inject(PICFLOW_CHECKOUT);
  private dragDropService = inject(DragDropService);
  public growthBook = inject(GROWTHBOOK);
  readonly gallery = input<GalleryDto>(undefined);

  @Input() darkBackground: boolean;
  @Input() titleAndDescription: string;
  @Output() onEditStart = new EventEmitter();
  @ViewChild('cover', { static: false }) cover: ElementRef<HTMLDivElement>;
  @ViewChild('sizeMenu', { static: false }) sizeMenu: ContextMenuComponent;
  @ViewChild('overlayMenu', { static: false }) overlayMenu: ContextMenuComponent;
  @ViewChild('coverItemMenu', { static: false }) coverItemMenu: ContextMenuComponent;
  @ViewChild('focalMenu', { static: false }) focalMenu: ContextMenuComponent;
  @ViewChild('textPositionMenu', { static: false }) textPositionMenu: ContextMenuComponent;
  public coverPreviewAsset = toSignal(
    combineLatest([
      this.dragDropService.dragging$.pipe(distinctUntilChanged((a, b) => a?.id === b?.id)),
      this.dragDropService.hovering$.pipe(
        map(hovering => {
          if (!hovering) return false;
          if (hovering.type !== 'cover') return false;
          return hovering.id === this.gallery().id;
        }),
        distinctUntilChanged()
      ),
    ]).pipe(
      map(([dragging, hovering]) => {
        if (!hovering) return;
        if (!dragging) return;
        if (dragging.type !== 'asset') return;
        return dragging.id;
      }),
      switchMap(assetId => {
        if (!assetId) return of(undefined);
        return this.assetService.getAsset(assetId, this.gallery().id).pipe(
          map(asset => {
            if (!canDisplayAsset(asset)) return undefined;
            return asset;
          })
        );
      })
    )
  );
  private destroyed$ = new Subject<void>();

  private coverDropWatcher = this.dragDropService.dropped$
    .pipe(
      takeUntil(this.destroyed$),
      filter(event => {
        if (!event) return false;
        if (event.type !== 'asset') return false;
        if (event.targetType !== 'cover') return false;
        return event.target === this.gallery().id;
      }),
      switchMap(event => {
        return this.assetService.getAsset(event.id, this.gallery().id).pipe(take(1));
      })
    )
    .subscribe(asset => {
      this.convertAssetToCover(asset);
    });

  theme = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    return gallery.theme || calcLegacyTheme(gallery);
  });

  themeWithPreview = computed(() => {
    const theme = this.theme();
    const previewAsset = this.coverPreviewAsset();
    if (!theme) return;
    if (!previewAsset) return theme;

    return {
      ...theme,
      cover: {
        ...theme.cover,
        ...this.galleryThemeService.getCoverDesignChangeParams(theme, theme.cover.design || 'simple').cover,
      },
    };
  });

  enableCoverShowMode = computed(() => {
    const gallery = this.gallery();
    const theme = this.themeWithPreview();
    if (!gallery || !theme) return false;
    return !!theme.cover.design;
  });

  position = signal<Rect>(undefined);
  galleryPermissions = input.required<GalleryDto['permissions']>();
  title = signal('\n');
  description = signal('\n');
  forceOpenEditMenu = false;
  uploadingCover = signal(false);
  textPositions: [number, number][] = [
    [0, 0],
    [0.5, 0],
    [1, 0],
    [0, 0.5],
    [0.5, 0.5],
    [1, 0.5],
    [0, 1],
    [0.5, 1],
    [1, 1],
  ];

  async ngOnChanges(changes: SimpleChanges) {
    const vals = this.gallery()
      .cover.position?.split(' ')
      .map(v => parseInt(v.split('%')[0])) || [50, 50];
    this.position.set(new Rect(vals[0], vals[1]));

    const galleryChanged = changes.gallery && changes.gallery.currentValue?.id !== changes.gallery.previousValue?.id;
    const focusedOnInput = document.activeElement?.classList.contains('input');
    if (!focusedOnInput || galleryChanged) {
      this.title.set(this.gallery().title || '\n');
      this.description.set(this.cleanUserGeneratedHtmlService.clean(this.gallery().description) || '\n');
    }
  }

  openThemeMenu(typographyMenu: string) {
    this.dialog.open(GalleryThemeMenuComponent, {
      gallery: this.gallery(),
      subPage: 'typography',
      openTypographyMenu: typographyMenu,
    });
  }

  textChange() {
    const gallery = this.gallery();
    this.galleryService.optimisticUpdateGallery(gallery, {
      title: getHtmlText(this.title()).trim(),
      description: fixContentEditableStyles(this.description()).trim(),
    });
    const title = `${getHtmlText(this.title()).trim() || gallery.name || 'Edit Gallery'} · Picflow`;
    this.meta.setTitle(title);
    this.saveTextDebounced();
  }

  saveTextDebounced = debounce(() => {
    this.updateText();
  }, 500);

  async updateText() {
    await this.galleryService.updateGallery(this.gallery(), {
      title: getHtmlText(this.title()).trim(),
      description: fixContentEditableStyles(this.description()).trim(),
    });
  }

  dropAsset(files: File[]) {
    this.uploadCover(files[0]);
  }

  async uploadCoverEvent(target: EventTarget) {
    const files = (target as HTMLInputElement).files;
    this.uploadCover(files[0]);
  }

  async uploadCover(file: File) {
    if (!file) return;

    if (this.fileIsVideo(file) && !this.allowSetVideoAsCover()) {
      return this.checkoutService.open(billingScopes.gallery.videoInCover);
    }

    this.uploadingCover.set(true);
    const resp = await this.galleryMetaImageUploadService.upload(file, this.gallery(), 'cover');

    this.tenantService.refreshTenant();
    if (resp) {
      this.analytics.track('upload_cover');
      const gallery = this.gallery();
      gallery.cover.overlay_color = hexToRgb(gallery.cover.overlay_color);
      this.autoSetPosition();
      const _resp = await this.galleryService.updateGallery(gallery, {
        cover: resp.cover,
        theme:
          this.theme().cover.design === null
            ? this.galleryThemeService.getCoverDesignChangeParams(this.theme(), 'simple')
            : undefined,
      });
      gallery.cover = _resp.cover;
      this.autoSetTeaser();
    } else {
      this.toasts.add('Failed to upload cover', 'error');
    }
    this.uploadingCover.set(false);
  }

  deleteCoverAsset() {
    const gallery = this.gallery();
    gallery.cover.uuid = null;
    gallery.cover.ext = null;
    gallery.cover.format = null;
    gallery.cover.video_origin = null;
    this.save();
  }

  private setPosition(x: number, y: number) {
    this.position.set(new Rect(x, y));
    const gallery = this.gallery();
    this.galleryService.optimisticUpdateGallery(gallery, {
      cover: { ...gallery.cover, position: `${this.position().x}% ${this.position().y}%` },
    });
  }

  private setPositionFromEvent(event: MouseEvent, el: HTMLElement) {
    this.setPosition(
      Math.round((100 / el.clientWidth) * event.offsetX),
      Math.round((100 / el.clientHeight) * event.offsetY)
    );
  }

  mouseDown(event: MouseEvent, el: HTMLElement) {
    event.preventDefault();
    this.setPositionFromEvent(event, el);
  }

  mouseMove(event: MouseEvent, el: HTMLElement) {
    if (event.buttons !== 1) return;
    event.preventDefault();
    this.setPositionFromEvent(event, el);
  }

  touchStart(event: TouchEvent, el: HTMLElement) {
    event.preventDefault();
    const target = <HTMLElement>event.target;
    const rect = target.getBoundingClientRect();
    const x = event.targetTouches[0].pageX - rect.left;
    const y = event.targetTouches[0].pageY - rect.top;

    this.setPosition(Math.round((100 / el.clientWidth) * x), Math.round((100 / el.clientHeight) * y));
    return false;
  }

  touchMove(event: TouchEvent, el: HTMLElement) {
    event.preventDefault();
    const target = <HTMLElement>event.target;
    const rect = target.getBoundingClientRect();
    const x = event.targetTouches[0].pageX - rect.left;
    const y = event.targetTouches[0].pageY - rect.top;

    this.setPosition(Math.round((100 / el.clientWidth) * x), Math.round((100 / el.clientHeight) * y));
    return false;
  }

  private getUpdateParams() {
    const cover = copy(this.gallery().cover);
    cover.overlay_color = hexToRgba(cover.overlay_color);

    // converting from rgb to rgba
    if (cover.overlay_color.includes('rgb(')) {
      cover.overlay_color = cover.overlay_color.split('rgb(').join('rgba(');
      cover.overlay_color = cover.overlay_color.split(')').join(',1)');
    }
    if (cover.overlay_color.split(',').pop() === '0)') {
      cover.overlay_color = cover.overlay_color.split(',0)').join(',0.001)');
    }
    return cover;
  }

  async save() {
    const cover = this.getUpdateParams();
    const resp = await this.galleryService.updateGallery(this.gallery(), {
      cover,
      theme:
        this.theme().cover.design === null
          ? this.galleryThemeService.getCoverDesignChangeParams(this.theme(), 'simple')
          : undefined,
    });
    this.autoSetTeaser();
    return resp;
  }

  colorPickerChange(color: string) {
    this.galleryService.optimisticUpdateGallery(this.gallery(), {
      theme: { ...this.theme(), cover: { ...this.theme().cover, overlay_color: color } },
    });
    this.saveThemeDebounced(color);
  }

  saveThemeDebounced = debounce(color => {
    this.galleryService.updateGallery(this.gallery(), {
      theme: { ...this.theme(), cover: { ...this.theme().cover, overlay_color: color } },
    });
  }, 1000);

  saveDebounced = debounce(() => {
    this.save();
  }, 1000);

  onToggleFocal(open: boolean) {
    if (!open) this.save();
  }

  changeTextPosition(position: [number, number]) {
    this.galleryService.updateGallery(this.gallery(), {
      theme: { ...this.theme(), cover: { ...this.theme().cover, content_position: position } },
    });
  }

  saveCoverSize(size: 'small' | 'medium' | 'large') {
    this.galleryService.updateGallery(this.gallery(), {
      theme: { ...this.theme(), cover: { ...this.theme().cover, size } },
    });
  }

  get tenant() {
    return this.gallery()?.tenant as TenantDto;
  }

  private async convertAssetToCover(asset: AssetDto) {
    if (!canDisplayAsset(asset)) return;
    if (asset.type === 'video' && !this.allowSetVideoAsCover()) {
      return this.checkoutService.open(billingScopes.gallery.videoInCover);
    }

    changeCoverAsset(this.gallery().cover, asset);

    this.analytics.track('set_asset_as_cover');

    this.autoSetPosition();
    await this.save();
  }

  private autoSetPosition() {
    this.gallery().cover.position = this.getAutoPotition();
  }

  private getAutoPotition() {
    this.setPosition(50, 50);
    return `50% 50%`;
  }

  private async autoSetTeaser() {
    await sleep(0);
    const gallery = this.gallery();
    if (!gallery.cover?.uuid) return;
    if (gallery.cover.uuid === gallery.teaser?.uuid) return;
    await this.galleryService.updateGallery(gallery, {
      teaser: {
        ...gallery.cover,
      },
    });
  }

  openEdit(type: string) {
    if (type === 'size') this.sizeMenu.open();
    if (type === 'overlay') this.overlayMenu.open();
    if (type === 'focal') this.focalMenu.open();
    if (type === 'textPosition') this.textPositionMenu.open();
    if (type === 'coverItem') this.coverItemMenu.open();
  }

  onTitleFontChange(fontFamily: string) {
    const params = { ...this.theme().cover.title_typography, font_family: fontFamily };

    this.galleryThemeService
      .getExtraFontFamilyUpdateParams(params, this.theme().cover.title_typography, false)
      .pipe(take(1))
      .subscribe(adjustedParams => {
        this.galleryService.optimisticUpdateGallery(this.gallery(), {
          theme: {
            ...this.theme(),
            cover: {
              ...this.theme().cover,
              title_typography: {
                ...this.theme().cover.title_typography,
                ...adjustedParams,
              },
            },
          },
        });
        this.debouncedSaveTheme();
      });
  }

  onTitleFontSizeChange(size: 'small' | 'medium' | 'large') {
    const params: UpdateGalleryBodyDto = {
      theme: {
        ...this.theme(),
        cover: {
          ...this.theme().cover,
          title_typography: { ...this.theme().cover.title_typography, font_size: this.convertSize(size) },
        },
      },
    };
    this.galleryService.optimisticUpdateGallery(this.gallery(), params);
    this.debouncedSaveTheme();
  }

  onTitleFontWeightChange(weight: string) {
    const params: UpdateGalleryBodyDto = {
      theme: {
        ...this.theme(),
        cover: {
          ...this.theme().cover,
          title_typography: { ...this.theme().cover.title_typography, font_weight: weight },
        },
      },
    };
    this.galleryService.optimisticUpdateGallery(this.gallery(), params);
    this.debouncedSaveTheme();
  }

  onTitleTextColorChange(color: string) {
    const params: UpdateGalleryBodyDto = {
      theme: {
        ...this.theme(),
        cover: {
          ...this.theme().cover,
          title_typography: { ...this.theme().cover.title_typography, font_color: color },
        },
      },
    };
    this.galleryService.optimisticUpdateGallery(this.gallery(), params);
    this.debouncedSaveTheme();
  }

  onParagraphFontChange(fontFamily: string) {
    const params = { ...this.theme().cover.paragraph_typography, font_family: fontFamily };

    this.galleryThemeService
      .getExtraFontFamilyUpdateParams(params, this.theme().cover.paragraph_typography, false)
      .pipe(take(1))
      .subscribe(adjustedParams => {
        this.galleryService.optimisticUpdateGallery(this.gallery(), {
          theme: {
            ...this.theme(),
            cover: {
              ...this.theme().cover,
              paragraph_typography: {
                ...this.theme().cover.paragraph_typography,
                ...adjustedParams,
              },
            },
          },
        });
        this.debouncedSaveTheme();
      });
  }

  onParagraphFontSizeChange(size: 'small' | 'medium' | 'large') {
    const params: UpdateGalleryBodyDto = {
      theme: {
        ...this.theme(),
        cover: {
          ...this.theme().cover,
          paragraph_typography: { ...this.theme().cover.paragraph_typography, font_size: this.convertSize(size) },
        },
      },
    };
    this.galleryService.optimisticUpdateGallery(this.gallery(), params);
    this.debouncedSaveTheme();
  }

  onParagraphFontWeightChange(weight: string) {
    const params: UpdateGalleryBodyDto = {
      theme: {
        ...this.theme(),
        cover: {
          ...this.theme().cover,
          paragraph_typography: { ...this.theme().cover.paragraph_typography, font_weight: weight },
        },
      },
    };

    this.galleryService.optimisticUpdateGallery(this.gallery(), params);
    this.debouncedSaveTheme();
  }

  onParagraphTextColorChange(color: string) {
    const params: UpdateGalleryBodyDto = {
      theme: {
        ...this.theme(),
        cover: {
          ...this.theme().cover,
          paragraph_typography: { ...this.theme().cover.paragraph_typography, font_color: color },
        },
      },
    };
    this.galleryService.optimisticUpdateGallery(this.gallery(), params);
    this.debouncedSaveTheme();
  }

  getFontSize(size: number) {
    if (size === 1) return 'small';
    if (size === 2) return 'medium';
    if (size === 3) return 'large';
    if (size === 4) return 'huge';
  }

  convertSize(size: 'small' | 'medium' | 'large' | 'huge') {
    if (size === 'small') return 1;
    if (size === 'medium') return 2;
    if (size === 'large') return 3;
    if (size === 'huge') return 4;
  }

  private fileIsVideo(file: File) {
    if (!file) return false;
    if (file.type.startsWith('video/')) return true;
    const extensinon = file.name.split('.').pop().toLowerCase();
    return videoFormats.includes(extensinon);
  }

  private allowSetVideoAsCover() {
    return this.permissionService.canDisplayElement(billingScopes.gallery.videoInCover);
  }

  private debouncedSaveTheme = debounce(() => this.saveTheme(), 500);

  private saveTheme() {
    this.galleryService.updateGallery(this.gallery(), { theme: this.gallery().theme });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
